import { ApiResponse } from 'apisauce';
import api from '../utils/api/api.service';
import errorHelper from '../utils/api/error-helper.service';
import BrokerMapper from '../../mappers/broker.mapper';
import { BrokerModel } from '../../models/broker.model';
import { BrokerUiModel } from '../../ui-models/broker.ui-model';
import { ErrorInfo } from '../../interfaces/error-info.interface';
import { ResponseModel } from '../../interfaces/response-model.interface';

/**
 * Brokers: the individuals and agencies that bring us guests and are paid
 * commission for it.
 *
 * In the platform layer rather than inside BrokerModule because two modules
 * read it -- BrokerModule manages the list, and the reservation create form
 * offers it whenever the segment is BROKER.
 */
const MODULE_NAME = 'broker';

export function getAllBrokers(): Promise<BrokerUiModel[] | ErrorInfo> {
  return api.getBrokers().then((response: ApiResponse<ResponseModel<BrokerModel[]>, ErrorInfo>) => {
    if (response.ok) {
      const { data } = response;
      if (data && data.data) {
        return BrokerMapper.mapToUIList(data.data);
      }
      return errorHelper(0, '', MODULE_NAME, (api.getBrokers as any).displayName);
    }

    return errorHelper(
      Number(response.status),
      response.data && response.data.errorCode
        ? String(response.data.errorCode || '')
        : String(response.problem || ''),
      MODULE_NAME,
      (api.getBrokers as any).displayName
    );
  });
}

export function addNewBroker(broker: BrokerUiModel): Promise<BrokerUiModel | ErrorInfo> {
  return api
    .createBroker(BrokerMapper.mapToModel(broker))
    .then((response: ApiResponse<ResponseModel<BrokerModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return BrokerMapper.mapToUI(data.data);
        }
        return errorHelper(0, '', MODULE_NAME, (api.createBroker as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data && response.data.errorCode
          ? String(response.data.errorCode || '')
          : String(response.problem || ''),
        MODULE_NAME,
        (api.createBroker as any).displayName
      );
    });
}

export function updateBroker(
  brokerId: number,
  broker: Partial<BrokerUiModel>
): Promise<BrokerUiModel | ErrorInfo> {
  return api
    .updateBroker(brokerId, broker as Partial<BrokerModel>)
    .then((response: ApiResponse<ResponseModel<BrokerModel>, ErrorInfo>) => {
      if (response.ok) {
        const { data } = response;
        if (data && data.data) {
          return BrokerMapper.mapToUI(data.data);
        }
        return errorHelper(0, '', MODULE_NAME, (api.updateBroker as any).displayName);
      }

      return errorHelper(
        Number(response.status),
        response.data && response.data.errorCode
          ? String(response.data.errorCode || '')
          : String(response.problem || ''),
        MODULE_NAME,
        (api.updateBroker as any).displayName
      );
    });
}

export function deleteBroker(brokerId: number): Promise<{} | ErrorInfo> {
  return api.deleteBroker(brokerId).then((response: ApiResponse<ResponseModel<{}>, ErrorInfo>) => {
    if (response.ok) {
      return {};
    }

    return errorHelper(
      Number(response.status),
      response.data && response.data.errorCode
        ? String(response.data.errorCode || '')
        : String(response.problem || ''),
      MODULE_NAME,
      (api.deleteBroker as any).displayName
    );
  });
}
